import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { productsApiUrl } from "../constants/endpoints";
import { RoughNotation } from "react-rough-notation";
import { useTranslation } from "react-i18next";
import { BsCart3 } from "react-icons/bs";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { productTestimonials } from "../constants/product-testimonials";
import talabaTnMobileImage from "../assets/img/talabatn-mobile.png";
import { cartService } from "../services/cartService";
import SideCart from "../components/SideCart";
import CartIcon from "../components/CartIcon";

const ProductsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState({});
  const [showSideCart, setShowSideCart] = useState(false);

  // Check if current language is Arabic
  const isRTL = i18n.language === 'ar';

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${productsApiUrl}/public_app/products?per_page=6`);
      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }
      const data = await response.json();
      setProducts(data.items || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const formatPrice = (price, discountPercentage) => {
    const finalPrice = price * (1 - discountPercentage / 100);
    return finalPrice.toFixed(2);
  };

  const handleAddToCart = async (productId, e) => {
    e.stopPropagation();
    try {
      setAddingToCart((prev) => ({ ...prev, [productId]: true }));
      await cartService.addToCart(productId, 1);
      
      // Show side cart when item is added
      setShowSideCart(true);
    } catch (err) {
      console.error("Error adding to cart:", err);
      // Could show error toast here
    } finally {
      setAddingToCart((prev) => ({ ...prev, [productId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="pd-top-150">
        <div className="container">
          <div className="text-center">
            <h3>{t("Loading products...")}</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pd-top-150">
        <div className="container">
          <div className="text-center">
            <h3 style={{marginBlock: '100px'}}>
              {t("Error loading products")}: {'Veuillez réessayer plus tard.'}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pd-top-150">
      {/* Cart Icon - Fixed Position */}
      <CartIcon
        onClick={() => setShowSideCart(true)}
        style={{
          position: 'fixed',
          top: window.innerWidth <= 768 ? '120px' : '170px',
          [isRTL ? 'left' : 'right']: window.innerWidth <= 768 ? '15px' : '20px',
          zIndex: 998,
          width: window.innerWidth <= 768 ? '45px' : '50px',
          height: window.innerWidth <= 768 ? '45px' : '50px'
        }}
      />

      <div className="container">
        <div className="about-area-inner">
          <div className="row">
            {i18n.language === "ar" ? (
              <>
                <div className="col-lg-3" />
                <div className="col-lg-9 text-right about-animate-right pb-0">
                  <div className="about-inner-wrap pt-5 pt-lg-3 mt-5 mt-lg-0">
                    <div className="section-title mb-0">
                      <h2 className="title pb-2">{t("our products")}</h2>
                      <h4 style={{ fontWeight: "normal" }} className="pb-2">
                        {t("products page description")}
                      </h4>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="col-lg-12 about-animate-right pb-0">
                <div className="about-inner-wrap pt-5 pt-lg-3 mt-5 mt-lg-0">
                  <div className="section-title mb-0">
                    <h2 className="title pb-2">{t("our products")}</h2>
                    <h4 style={{ fontWeight: "normal" }} className="pb-2">
                      {t("products page description")}
                    </h4>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Products Section Header with Cart Icon Alternative */}
          <div className="col-lg-12 about-animate-right pb-0">
            <div className="about-inner-wrap pt-5 pt-lg-3 mt-5 mt-lg-0">
              <div 
                className="section-title mb-4 text-center"
                style={{ position: 'relative' }}
              >
                {/* Alternative Cart Icon in Section Header */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    display: 'none' // Hidden for now, using fixed position instead
                  }}
                >
                  <CartIcon
                    onClick={() => setShowSideCart(true)}
                    style={{
                      width: '45px',
                      height: '45px'
                    }}
                  />
                </div>

                <h3 style={{ fontWeight: "lighter" }} className="title pb-2">
                  <RoughNotation
                    animate="true"
                    type="underline"
                    show={true}
                    color="#9143DA"
                    animationDelay={1000}
                    animationDuration={2500}
                    className="text-slate-200"
                  >
                    {t("revision books")}&nbsp;
                  </RoughNotation>
                </h3>{/* 
                <p
                  style={{
                    fontWeight: "normal",
                    color: "#666",
                    fontSize: "16px",
                  }}
                  className="pb-3"
                >
                  {t("revision books description")}
                </p> */}
              </div>
            </div>
          </div>

          <div className="row">
            {products.length > 0 ? (
              products.map((product, index) => (
                <div key={index} className="col-lg-6 col-md-12 mb-4">
                  <div
                    className="product-card"
                    onClick={() => navigate(`/products/${product.id}`)}
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "15px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: window.innerWidth <= 768 ? "column" : "row", // Stack vertically on mobile
                      height: window.innerWidth <= 768 ? "auto" : "350px", // Auto height on mobile
                      position: "relative",
                      border: "1px solid rgba(0, 0, 0, 0.05)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 30px rgba(0, 0, 0, 0.15)";
                      // Show overlay on image
                      const overlay = e.currentTarget.querySelector(
                        ".image-hover-overlay"
                      );
                      if (overlay) {
                        overlay.style.opacity = "1";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 20px rgba(0, 0, 0, 0.08)";
                      // Hide overlay on image
                      const overlay = e.currentTarget.querySelector(
                        ".image-hover-overlay"
                      );
                      if (overlay) {
                        overlay.style.opacity = "0";
                      }
                    }}
                  >
                    <div
                      style={{
                        width: window.innerWidth <= 768 ? "100%" : "50%", // Full width on mobile
                        height: window.innerWidth <= 768 ? "200px" : "100%", // Fixed height on mobile
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Image Hover Overlay */}
                      <div
                        className="image-hover-overlay"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: 0,
                          transition: "opacity 0.3s ease",
                          zIndex: 10,
                        }}
                      >
                        <div
                          style={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: "600",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                          <span>{t("See more details")}</span>
                        </div>
                      </div>
                      {product.images.length > 0 ? (
                        <img
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            backgroundColor: "#f8f9fa",
                          }}
                          src={`https://file.talabatn.net/${product.images[0].image_id}`}
                          alt={product.name}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#6c757d",
                          }}
                        >
                          <span>{t("No Image")}</span>
                        </div>
                      )}
                      {product.discount_percentage > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            padding: "5px 10px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "bold",
                          }}
                        >
                          -{product.discount_percentage}%
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        width: window.innerWidth <= 768 ? "100%" : "50%", // Full width on mobile
                        padding: window.innerWidth <= 768 ? "15px" : "20px", // Reduced padding on mobile
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        minHeight: window.innerWidth <= 768 ? "200px" : "auto", // Ensure minimum height on mobile
                      }}
                    >
                      <h4
                        style={{
                          fontSize: window.innerWidth <= 768 ? "16px" : "18px", // Smaller font on mobile
                          fontWeight: "bold",
                          color: "#2c3e50",
                          margin: "0 0 10px 0",
                          lineHeight: "1.3",
                        }}
                      >
                        {product.name}
                      </h4>
                      <p
                        style={{
                          fontSize: window.innerWidth <= 768 ? "12px" : "13px", // Smaller font on mobile
                          color: "#666",
                          margin: "0 0 10px 0",
                          lineHeight: "1.4",
                          flex: 1,
                        }}
                      >
                        {product.description && product.description.length > (window.innerWidth <= 768 ? 40 : 60)
                          ? `${product.description.substring(0, window.innerWidth <= 768 ? 40 : 60)}...`
                          : product.description || ""}
                      </p>
                      {(product.institut_grade ||
                        product.institut_speciality) && (
                        <div
                          style={{
                            marginBottom: "8px",
                            color: "#6c757d",
                            fontSize: window.innerWidth <= 768 ? "12px" : "14px", // Smaller font on mobile
                            lineHeight: "1.4",
                          }}
                        >
                          {product.institut_grade}
                          {product.institut_grade && product.institut_speciality
                            ? " "
                            : ""}
                          {product.institut_speciality}
                        </div>
                      )}
                      {product.subject && (
                        <div style={{ marginBottom: "8px" }}>
                          <span
                            style={{
                              backgroundColor:
                                product.subject.color || "#9143DA",
                              color: "white",
                              padding: "3px 6px",
                              borderRadius: "8px",
                              fontSize: window.innerWidth <= 768 ? "9px" : "10px", // Smaller font on mobile
                              fontWeight: "500",
                            }}
                          >
                            {product.subject.name}
                          </span>
                        </div>
                      )}
                      <div
                        style={{
                          marginBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap", // Allow wrapping on mobile
                        }}
                      >
                        {product.discount_percentage > 0 ? (
                          <>
                            <span
                              style={{
                                fontSize: window.innerWidth <= 768 ? "14px" : "17px", // Smaller font on mobile
                                color: "#6c757d",
                                textDecoration: "line-through",
                                marginRight: "5px",
                              }}
                            >
                              {product.price} {t("TND")}
                            </span>
                            <span
                              style={{
                                fontSize: window.innerWidth <= 768 ? "18px" : "26px", // Smaller font on mobile
                                fontWeight: "bold",
                                color: "#9143DA",
                              }}
                            >
                              {formatPrice(
                                product.price,
                                product.discount_percentage
                              )}{" "}
                              {t("TND")}
                            </span>
                          </>
                        ) : (
                          <span
                            style={{
                              fontSize: window.innerWidth <= 768 ? "18px" : "26px", // Smaller font on mobile
                              fontWeight: "bold",
                              color: "#2c3e50",
                            }}
                          >
                            {product.price} {t("TND")}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleAddToCart(product.id, e)}
                        disabled={addingToCart[product.id]}
                        style={{
                          backgroundColor: addingToCart[product.id]
                            ? "#ccc"
                            : "#9143DA",
                          color: "white",
                          border: "none",
                          padding: window.innerWidth <= 768 ? "10px 12px" : "8px 16px", // Larger padding on mobile
                          borderRadius: "8px",
                          fontSize: window.innerWidth <= 768 ? "13px" : "14px", // Slightly smaller font on mobile
                          fontWeight: "500",
                          cursor: addingToCart[product.id]
                            ? "not-allowed"
                            : "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          width: "100%", // Full width button on mobile
                          marginTop: "auto", // Push button to bottom
                        }}
                      >
                        {addingToCart[product.id] ? (
                          <>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                border: "2px solid white",
                                borderTop: "2px solid transparent",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            />
                            {t("Adding...")}
                          </>
                        ) : (
                          <>
                            <BsCart3 size={window.innerWidth <= 768 ? 14 : 16} color="white" /> {t("Achetez")}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-lg-12 text-center">
                <h4>{t("No products available at the moment")}</h4>
              </div>
            )}
          </div>
        </div>
        <div
          className="pd-top-60 pd-bottom-60"
          style={{ backgroundColor: "white" }}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-12 about-animate-right pb-0">
                <div className="about-inner-wrap pt-5 pt-lg-3 mt-5 mt-lg-0">
                  <div className="section-title mb-4 text-center">
                    <h3
                      style={{ fontWeight: "lighter" }}
                      className="title pb-2"
                    >
                      <RoughNotation
                        animate="true"
                        type="underline"
                        show={true}
                        color="#9143DA"
                        animationDelay={1000}
                        animationDuration={2500}
                        className="text-slate-200"
                      >
                        {t("books testimonials title")}&nbsp;
                      </RoughNotation>
                    </h3>
                    <p
                      style={{
                        fontWeight: "normal",
                        color: "#666",
                        fontSize: "16px",
                      }}
                      className="pb-3"
                    >
                      {t("books testimonials description")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <Swiper
              slidesPerView={window.innerWidth <= 768 ? 1 : 2}
              spaceBetween={30}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
              }}
              loop={true}
              modules={[Autoplay, Pagination]}
              className="mySwiper"
              style={{
                "--swiper-pagination-color": "#AF85DC",
                "--swiper-pagination-bullet-inactive-color": "#DEBDFF",
                "--swiper-pagination-bullet-size": "12px",
                "--swiper-pagination-bullet-horizontal-gap": "6px",
                paddingBottom: "50px",
              }}
            >
              {productTestimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "40px 35px 50px",
                      minHeight: "280px",
                      maxHeight: "320px",
                      border: "1px solid #f0e6ff",
                      boxShadow: "0 5px 15px rgba(103, 58, 183, 0.08)",
                      position: "relative",
                      overflow: "visible",
                      transition: "all 0.3s ease",
                      marginTop: "40px",
                      display: "flex",
                      flexDirection: "column",
                      ":hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 8px 25px rgba(103, 58, 183, 0.15)",
                      },
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "-40px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "4px solid white",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                        background: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                      }}
                    >
                      <AccountCircleIcon
                        style={{ fontSize: "60px", color: "#9143DA" }}
                      />
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        right: "20px",
                        bottom: "5px",
                        color: "#f0e6ff",
                        fontSize: "250px",
                        lineHeight: 1,
                        fontFamily: "Cormorant Garamond, serif",
                        opacity: 0.6,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    >
                      ❝
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          margin: "10px 0 15px",
                          justifyContent: "flex-start",
                        }}
                      >
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill={
                              i < testimonial.rating ? "#ffc107" : "#e0e0e0"
                            }
                          >
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                        ))}
                      </div>
                      <p
                        style={{
                          fontSize: "15px",
                          lineHeight: "1.6",
                          color: "#555",
                          marginBottom: "20px",
                          flex: "1",
                          fontStyle: "italic",
                          position: "relative",
                          paddingLeft: "10px",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: "4",
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {testimonial.quote}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          paddingTop: "12px",
                          marginTop: "auto",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            color: "#333",
                            fontSize: "15px",
                          }}
                        >
                          {testimonial.author}
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      {/*  <div
          className="pd-top-60 pd-bottom-60"
          style={{ position: "relative", padding: "200px 0" }}
        >
          // Hide mobile phone image on mobile devices 
          {window.innerWidth > 768 && (
            <div
              style={{
                position: "absolute",
                left: "2%",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                width: "400px",
                maxWidth: "28vw",
              }}
            >
              <img
                src={talabaTnMobileImage}
                alt="Talaba TN Mobile App"
                style={{
                  width: "100%",
                  height: "auto",
                  filter: "drop-shadow(0 10px 25px rgba(0, 0, 0, 0.2))",
                  transform: "rotate(-2deg)",
                }}
              />
            </div>
          )}
           <div
            style={{
              backgroundColor: "#1a1a1a",
              borderRadius: "0",
              padding: window.innerWidth <= 768 ? "60px 20px" : "80px 40px",
              color: "white",
              position: "relative",
              overflow: "visible",
              width: "100vw",
              marginLeft: "calc(-50vw + 50%)",
              marginRight: "calc(-50vw + 50%)",
              display: "flex",
              justifyContent: window.innerWidth <= 768 ? "center" : "flex-end",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: window.innerWidth <= 768 ? "100%" : "55%",
                position: "relative",
                zIndex: 2,
                textAlign: window.innerWidth <= 768 ? "center" : "left",
                paddingRight: window.innerWidth <= 768 ? "0" : "8%",
              }}
            >
              <h2
                style={{
                  fontSize: window.innerWidth <= 768 ? "1.5rem" : "2rem",
                  fontWeight: "bold",
                  marginBottom: "20px",
                  lineHeight: "1.3",
                  paddingTop: "5px",
                }}
              >
                {i18n.language === "ar" ? (
                  <>
                    <span
                      style={{
                        color: "white",
                        fontSize: window.innerWidth <= 768 ? "1rem" : "1rem",
                      }}
                    >
                      {t("Disponible maintenant sur Play Store.")}
                    </span>{" "}
                    <span
                      style={{
                        background:
                          "linear-gradient(135deg, #9143DA 0%, #c471ed 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        paddingTop: "2px",
                        display: "inline-block",
                      }}
                    >
                      {t("Application Talaba TN")}
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      style={{
                        background:
                          "linear-gradient(135deg, #9143DA 0%, #c471ed 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        paddingTop: "2px",
                        display: "inline-block",
                      }}
                    >
                      {t("Application Talaba TN")}
                    </span>{" "}
                    <span
                      style={{
                        color: "white",
                        fontSize: window.innerWidth <= 768 ? "1rem" : "1rem",
                      }}
                    >
                      {t("Disponible maintenant sur Play Store.")}
                    </span>
                  </>
                )}
              </h2>

              <p
                style={{
                  fontSize: window.innerWidth <= 768 ? "1rem" : "1.2rem",
                  lineHeight: "1.6",
                  marginBottom: "30px",
                  color: "#b0b0b0",
                }}
              >
                {t(
                  "Télécharger notre application gratuitement et étudier avec charme et innovation."
                )}
              </p>
              <a
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  backgroundColor: "transparent",
                  border: "2px solid #9143DA",
                  borderRadius: "12px",
                  padding: window.innerWidth <= 768 ? "10px 20px" : "12px 30px",
                  textDecoration: "none",
                  color: "white",
                  fontSize: window.innerWidth <= 768 ? "14px" : "16px",
                  fontWeight: "600",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#9143DA";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <svg
                    width={window.innerWidth <= 768 ? "20" : "24"}
                    height={window.innerWidth <= 768 ? "20" : "24"}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.92 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  {t("GET IT ON Google Play")}
                </div>
              </a>
            </div>
            
            // Keep decorative elements only on desktop 
            {window.innerWidth > 768 && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: "-0px",
                    right: "-50px",
                    width: "100px",
                    height: "100px",
                    background:
                      "linear-gradient(135deg, #9143DA20 0%, #c471ed20 100%)",
                    borderRadius: "50%",
                    zIndex: 1,
                  }}
                ></div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "-20px",
                    left: "-80px",
                    width: "160px",
                    height: "160px",
                    background:
                      "linear-gradient(135deg, #9143DA15 0%, #c471ed15 100%)",
                    borderRadius: "50%",
                    zIndex: 1,
                  }}
                ></div>
              </>
            )}
          </div>
        </div> */}

        {/* Side Cart */}
        <SideCart
          isOpen={showSideCart}
          onClose={() => setShowSideCart(false)}
          autoCloseDelay={0} // Don't auto-close when opened via cart icon
        />

        {/* Add keyframes for loading spinner */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default ProductsPage;
